/**
 * Variables on Both Sides generator - the architecture's vertical slice.
 *
 * Strategy: choose the solution first, choose a structural family, then build
 * an equation that is true by construction. The equation is never "solved" to
 * find the answer; it is assembled around a known answer and then independently
 * re-validated. Quality filters reject ugly instances and the generator
 * regenerates with a derived seed until a clean instance is found.
 *
 * Families (see feasibility report):
 *   F1: ax + b = cx + d
 *   F2: a(x + b) = cx + d
 *   F3: a(x + b) + c = dx + e
 *   F4: ax + b = c(x + d)
 *   F5: a(bx + c) = d(ex + f)
 *   F6: a(x + b) - c(x + d) = e
 *   F7: a(bx + c) + d = e(fx + g) + h
 */

import { Rational, rat } from "../engine/rational";
import { Rng } from "../engine/rng";
import { LinearExpr, LinearEquation } from "../engine/linear";
import {
  validateEquation,
  crossCheckEquationPresentation,
} from "../engine/validate";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Hint,
  Question,
  QuestionSignature,
  SolutionStep,
} from "./types";
import { getTopic } from "../curriculum/curriculum";
import {
  getDefaultCalibration,
  featureFrequency,
  frequencyProbability,
  type CalibrationProfile,
} from "../curriculum/calibration";
import {
  deriveSolutionSteps,
  equationToLatex,
  equationToText,
} from "./equation-format";

const TOPIC = "variables-both-sides" as const;
const VERSION = "1.0.0";
const VARIABLES = ["x", "n", "r", "a", "v", "m", "p"] as const;

type FamilyId = "F1" | "F2" | "F3" | "F4" | "F5" | "F6" | "F7";
type Category = "one" | "none" | "infinite";

interface DifficultyConfig {
  readonly families: readonly FamilyId[];
  /** Range for small structural coefficients (a, c, factors). */
  readonly coeffMin: number;
  readonly coeffMax: number;
  /** Range for constants inside/around expressions. */
  readonly constMin: number;
  readonly constMax: number;
  /** Solution value pool bounds (integer solutions). */
  readonly solMin: number;
  readonly solMax: number;
  /** Whether fractional solutions may be produced at this level. */
  readonly allowFractionalSolution: boolean;
  /** Probability of choosing a negative where a sign is free. */
  readonly negativeBias: number;
}

const DIFFICULTY: Record<Difficulty, DifficultyConfig> = {
  easy: {
    families: ["F1", "F2", "F4"],
    coeffMin: 1,
    coeffMax: 5,
    constMin: 1,
    constMax: 9,
    solMin: 1,
    solMax: 9,
    allowFractionalSolution: false,
    negativeBias: 0.1,
  },
  standard: {
    families: ["F1", "F2", "F3", "F4", "F6"],
    coeffMin: 1,
    coeffMax: 7,
    constMin: 1,
    constMax: 12,
    solMin: -9,
    solMax: 9,
    allowFractionalSolution: false,
    negativeBias: 0.4,
  },
  hard: {
    families: ["F2", "F3", "F4", "F5", "F6", "F7"],
    coeffMin: 2,
    coeffMax: 9,
    constMin: 1,
    constMax: 12,
    solMin: -12,
    solMax: 12,
    allowFractionalSolution: true,
    negativeBias: 0.5,
  },
  challenge: {
    families: ["F5", "F6", "F7"],
    coeffMin: 2,
    coeffMax: 11,
    constMin: 1,
    constMax: 12,
    solMin: -12,
    solMax: 12,
    allowFractionalSolution: true,
    negativeBias: 0.55,
  },
};

/** A built instance: the displayed (possibly un-simplified) form + math. */
interface Built {
  readonly family: FamilyId;
  readonly variable: string;
  /** Displayed equation LaTeX (may contain parentheses before distributing). */
  readonly displayLatex: string;
  /** Displayed plain text for the reparse cross-check. */
  readonly displayText: string;
  /** The simplified canonical equation used for solving/validation. */
  readonly equation: LinearEquation;
  readonly category: Category;
  readonly solution?: Rational;
  /** Optional leading distribution step to prepend to derived steps. */
  readonly distributionStep?: SolutionStep;
}

export class VariablesBothSidesGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  private readonly calibration: CalibrationProfile | undefined;

  constructor(calibration = getDefaultCalibration(TOPIC)) {
    this.calibration = calibration;
  }

  generate(ctx: GenerateContext): Question {
    const cfg = DIFFICULTY[ctx.difficulty];
    // Regenerate with a derived seed until a clean, validated instance appears.
    const maxAttempts = 200;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const built = this.build(rng, cfg);
      if (!built) continue;
      const q = this.finalize(ctx, built);
      if (q) return q;
    }
    throw new Error(
      `variables-both-sides: failed to generate a valid question after ${maxAttempts} attempts (seed ${ctx.seed}, ${ctx.difficulty})`,
    );
  }

  signature(q: Question): QuestionSignature {
    return {
      generator: TOPIC,
      family: q.family,
      difficulty: q.difficulty,
      structure: this.structureKey(q),
    };
  }

  // --- internals ---------------------------------------------------------

  private structureKey(q: Question): string {
    const eq = q.math as LinearEquation | undefined;
    if (!eq) return `${q.family}:${q.answer.kind}`;
    return [
      q.family,
      eq.left.coeff.key(),
      eq.left.constant.key(),
      eq.right.coeff.key(),
      eq.right.constant.key(),
    ].join("|");
  }

  /** Choose category honoring calibration (never -> excluded). */
  private chooseCategory(rng: Rng): Category {
    const pNone = frequencyProbability(
      featureFrequency(this.calibration, "noSolutionCases", "never"),
    );
    const pInf = frequencyProbability(
      featureFrequency(this.calibration, "infiniteSolutionCases", "never"),
    );
    const r = rng.next();
    if (r < pNone) return "none";
    if (r < pNone + pInf) return "infinite";
    return "one";
  }

  /** Choose the target solution, respecting fractional-solution calibration. */
  private chooseSolution(rng: Rng, cfg: DifficultyConfig): Rational {
    const allowFrac =
      cfg.allowFractionalSolution &&
      rng.next() <
        frequencyProbability(
          featureFrequency(this.calibration, "fractionalSolutions", "never"),
        );
    const numerator = rng.intNonZero(cfg.solMin, cfg.solMax);
    if (!allowFrac) return rat(numerator);
    const den = rng.int(2, 4);
    return rat(numerator, den);
  }

  /**
   * Magnitude bound for the post-distribution canonical form. Distributing
   * families multiply parameters, so the canonical constants are naturally
   * larger than the displayed coefficients. Easy/Standard honor the classroom
   * calibration cap (small numbers); Hard/Challenge allow the larger products
   * their structure implies.
   */
  private maxMagnitude(difficulty: Difficulty): bigint {
    const perDifficulty: Record<Difficulty, number> = {
      easy: 60,
      standard: 90,
      hard: 200,
      challenge: 300,
    };
    let m = perDifficulty[difficulty];
    // The calibration cap only tightens the small-number levels.
    const cap = this.calibration?.maxCoefficientMagnitude;
    if (cap !== undefined && (difficulty === "easy" || difficulty === "standard")) {
      // Cap applies to the displayed structural coefficients; allow the
      // canonical form up to a modest multiple to accommodate distribution.
      m = Math.min(m, cap * 8);
    }
    return BigInt(m);
  }

  private build(rng: Rng, cfg: DifficultyConfig): Built | null {
    const family = rng.pick(cfg.families);
    const variable = rng.pick(VARIABLES);
    const category = this.chooseCategory(rng);

    if (category === "one") {
      return this.buildOneSolution(rng, cfg, family, variable);
    }
    return this.buildSpecial(rng, cfg, family, variable, category);
  }

  /**
   * Build a one-solution instance for the chosen family. We pick the target
   * solution s and free structural parameters, then compute the one remaining
   * parameter so the equation holds at s.
   */
  private buildOneSolution(
    rng: Rng,
    cfg: DifficultyConfig,
    family: FamilyId,
    v: string,
  ): Built | null {
    const s = this.chooseSolution(rng, cfg);
    const c = () => this.coeff(rng, cfg);
    const k = () => this.constant(rng, cfg);

    // Each family produces: left/right LinearExpr (simplified), plus a display
    // form (with parentheses) and an optional distribution step.
    switch (family) {
      case "F1": {
        // ax + b = cx + d,  a != c,  d = a*s + b - c*s
        const a = c();
        let cc = c();
        if (a.equals(cc)) cc = cc.add(rat(1)); // ensure a != c
        const b = k();
        const d = a.mul(s).add(b).sub(cc.mul(s));
        const left = new LinearExpr(a, b);
        const right = new LinearExpr(cc, d);
        const eq = new LinearEquation(left, right);
        return this.plain(family, v, eq, s);
      }
      case "F2": {
        // a(x + b) = cx + d  ->  ax + ab = cx + d
        const a = c();
        let cc = c();
        if (a.equals(cc)) cc = cc.add(rat(1));
        const b = k();
        const left = LinearExpr.variable()
          .add(LinearExpr.constantExpr(b))
          .scale(a);
        // d so that left(s) = cc*s + d
        const d = left.evaluate(s).sub(cc.mul(s));
        const right = new LinearExpr(cc, d);
        const eq = new LinearEquation(left, right);
        return this.withDistribution(
          family,
          v,
          `${factorLatex(a)}(${v} ${plusConst(b)}) = ${exprRight(cc, d, v)}`,
          `${factorText(a)}(${v} ${plusConstText(b)}) = ${exprRightText(cc, d, v)}`,
          eq,
          s,
          a,
          b,
        );
      }
      case "F3": {
        // a(x + b) + c = dx + e
        const a = c();
        const b = k();
        let dd = c();
        if (a.equals(dd)) dd = dd.add(rat(1));
        const cc = k();
        const left = LinearExpr.variable()
          .add(LinearExpr.constantExpr(b))
          .scale(a)
          .add(LinearExpr.constantExpr(cc));
        const e = left.evaluate(s).sub(dd.mul(s));
        const right = new LinearExpr(dd, e);
        const eq = new LinearEquation(left, right);
        return this.withDistribution(
          family,
          v,
          `${factorLatex(a)}(${v} ${plusConst(b)}) ${plusConst(cc)} = ${exprRight(dd, e, v)}`,
          `${factorText(a)}(${v} ${plusConstText(b)}) ${plusConstText(cc)} = ${exprRightText(dd, e, v)}`,
          eq,
          s,
          a,
          b,
        );
      }
      case "F4": {
        // ax + b = c(x + d)
        const a = c();
        let cc = c();
        if (a.equals(cc)) cc = cc.add(rat(1));
        const d = k();
        const right = LinearExpr.variable()
          .add(LinearExpr.constantExpr(d))
          .scale(cc);
        const b = right.evaluate(s).sub(a.mul(s));
        const left = new LinearExpr(a, b);
        const eq = new LinearEquation(left, right);
        return this.withDistribution(
          family,
          v,
          `${exprRight(a, b, v)} = ${factorLatex(cc)}(${v} ${plusConst(d)})`,
          `${exprRightText(a, b, v)} = ${factorText(cc)}(${v} ${plusConstText(d)})`,
          eq,
          s,
          cc,
          d,
        );
      }
      case "F5": {
        // a(bx + c) = d(ex + f)  ->  ensure ab != de
        const a = c();
        const b = c();
        const d = c();
        let e = c();
        if (a.mul(b).equals(d.mul(e))) e = e.add(rat(1));
        const cc = k();
        const left = new LinearExpr(rat(1), Rational.ZERO)
          .scale(b)
          .add(LinearExpr.constantExpr(cc))
          .scale(a); // a(bx + c)
        // Solve f so left(s) = d*(e*s + f) -> f = left(s)/d - e*s
        const f = left.evaluate(s).div(d).sub(e.mul(s));
        const right = LinearExpr.variable()
          .scale(e)
          .add(LinearExpr.constantExpr(f))
          .scale(d);
        const eq = new LinearEquation(left, right);
        // f may be fractional; that is acceptable structure at hard/challenge.
        return this.withDistribution(
          family,
          v,
          `${factorLatex(a)}(${coeff(b, v)} ${plusConst(cc)}) = ${factorLatex(d)}(${coeff(e, v)} ${plusConst(f)})`,
          `${factorText(a)}(${coeffText(b, v)} ${plusConstText(cc)}) = ${factorText(d)}(${coeffText(e, v)} ${plusConstText(f)})`,
          eq,
          s,
          a,
          cc,
        );
      }
      case "F6": {
        // a(x + b) - c(x + d) = e,  ensure a != c
        const a = c();
        let cc = c();
        if (a.equals(cc)) cc = cc.add(rat(1));
        const b = k();
        const d = k();
        const left = LinearExpr.variable()
          .add(LinearExpr.constantExpr(b))
          .scale(a)
          .sub(LinearExpr.variable().add(LinearExpr.constantExpr(d)).scale(cc));
        const e = left.evaluate(s);
        const right = LinearExpr.constantExpr(e);
        const eq = new LinearEquation(left, right);
        return this.withDistribution(
          family,
          v,
          `${factorLatex(a)}(${v} ${plusConst(b)}) ${groupOp(cc)}(${v} ${plusConst(d)}) = ${e.toLatex()}`,
          `${factorText(a)}(${v} ${plusConstText(b)}) ${groupOp(cc)}(${v} ${plusConstText(d)}) = ${e.toString()}`,
          eq,
          s,
          a,
          b,
        );
      }
      case "F7": {
        // a(bx + cIn) + d = e(fx + g) + h,  ensure ab != ef
        const a = c();
        const b = c();
        const cIn = k();
        const d = k();
        const e = c();
        let f = c();
        if (a.mul(b).equals(e.mul(f))) f = f.add(rat(1));
        const g = k();
        const left = LinearExpr.variable()
          .scale(b)
          .add(LinearExpr.constantExpr(cIn))
          .scale(a)
          .add(LinearExpr.constantExpr(d));
        const rightNoH = LinearExpr.variable()
          .scale(f)
          .add(LinearExpr.constantExpr(g))
          .scale(e);
        const h = left.evaluate(s).sub(rightNoH.evaluate(s));
        const right = rightNoH.add(LinearExpr.constantExpr(h));
        const eq = new LinearEquation(left, right);
        return this.withDistribution(
          family,
          v,
          `${factorLatex(a)}(${coeff(b, v)} ${plusConst(cIn)}) ${plusConst(d)} = ${factorLatex(e)}(${coeff(f, v)} ${plusConst(g)}) ${plusConst(h)}`,
          `${factorText(a)}(${coeffText(b, v)} ${plusConstText(cIn)}) ${plusConstText(d)} = ${factorText(e)}(${coeffText(f, v)} ${plusConstText(g)}) ${plusConstText(h)}`,
          eq,
          s,
          a,
          cIn,
        );
      }
    }
  }

  /** Build a no-solution or infinite-solution instance using F1-style shape. */
  private buildSpecial(
    rng: Rng,
    cfg: DifficultyConfig,
    family: FamilyId,
    v: string,
    category: Exclude<Category, "one">,
  ): Built | null {
    // Same variable coefficient on both sides. Constants equal => infinite,
    // unequal => none.
    const a = this.coeff(rng, cfg);
    const b = this.constant(rng, cfg);
    const d = category === "infinite" ? b : this.differentConstant(rng, cfg, b);
    const eq = new LinearEquation(
      new LinearExpr(a, b),
      new LinearExpr(a, d),
    );
    return {
      family,
      variable: v,
      displayLatex: equationToLatex(eq, v),
      displayText: equationToText(eq, v),
      equation: eq,
      category,
    };
  }

  // Builders for display + step assembly -------------------------------

  private plain(
    family: FamilyId,
    v: string,
    eq: LinearEquation,
    s: Rational,
  ): Built {
    return {
      family,
      variable: v,
      displayLatex: equationToLatex(eq, v),
      displayText: equationToText(eq, v),
      equation: eq,
      category: "one",
      solution: s,
    };
  }

  private withDistribution(
    family: FamilyId,
    v: string,
    displayLatex: string,
    displayText: string,
    eq: LinearEquation,
    s: Rational,
    _factor: Rational,
    _inner: Rational,
  ): Built {
    return {
      family,
      variable: v,
      displayLatex,
      displayText,
      equation: eq,
      category: "one",
      solution: s,
      distributionStep: {
        description: "Distribute and combine like terms on both sides.",
        latex: equationToLatex(eq, v),
      },
    };
  }

  private coeff(rng: Rng, cfg: DifficultyConfig): Rational {
    const mag = rng.int(cfg.coeffMin, cfg.coeffMax);
    const sign = rng.sign(cfg.negativeBias);
    return rat(mag * sign);
  }

  private constant(rng: Rng, cfg: DifficultyConfig): Rational {
    const mag = rng.int(cfg.constMin, cfg.constMax);
    const sign = rng.sign(cfg.negativeBias);
    return rat(mag * sign);
  }

  private differentConstant(
    rng: Rng,
    cfg: DifficultyConfig,
    avoid: Rational,
  ): Rational {
    let c = this.constant(rng, cfg);
    let guard = 0;
    while (c.equals(avoid) && guard++ < 20) c = this.constant(rng, cfg);
    if (c.equals(avoid)) c = c.add(rat(1));
    return c;
  }

  /** Validate independently, apply quality filters, and finalize a Question. */
  private finalize(ctx: GenerateContext, built: Built): Question | null {
    const { equation, category, solution } = built;

    // Independent validation.
    const maxMag = this.maxMagnitude(ctx.difficulty);
    const spec =
      category === "one"
        ? {
            expectedCategory: "one" as const,
            expectedValue: solution as Rational,
            maxAbsMagnitude: maxMag,
          }
        : { expectedCategory: category, maxAbsMagnitude: maxMag };
    const v = validateEquation(equation, spec);
    if (!v.ok) return null;

    // Presentation cross-check: the simplified reparse of the displayed text
    // must agree in solution with the internal equation. For distributing
    // families the displayed text contains parentheses, which the parser
    // handles, so this catches sign/format bugs in the display string.
    const cross = crossCheckEquationPresentation(
      equation,
      built.displayText,
      undefined,
    );
    if (!cross.ok) return null;

    // Quality filters.
    if (!this.qualityOk(built)) return null;

    // Recent-duplicate avoidance (best effort).
    const structure = this.structureKeyFromBuilt(built);
    if (
      ctx.recent?.some(
        (r) => r.generator === TOPIC && r.structure === structure,
      )
    ) {
      return null;
    }

    return this.toQuestion(ctx, built);
  }

  private structureKeyFromBuilt(b: Built): string {
    return [
      b.family,
      b.equation.left.coeff.key(),
      b.equation.left.constant.key(),
      b.equation.right.coeff.key(),
      b.equation.right.constant.key(),
    ].join("|");
  }

  private qualityOk(b: Built): boolean {
    // For one-solution problems avoid trivial answers (0) appearing; 0 is a
    // legitimate answer but should be rare, filtered probabilistically upstream
    // rather than here. Reject solution == 0 outright to keep things clean for
    // the current classroom calibration.
    if (b.category === "one" && b.solution && b.solution.isZero()) {
      return false;
    }
    // Reject degenerate display where a side simplified to bare "0 = 0" style
    // for one-solution problems (shouldn't happen, but guard).
    if (b.category === "one") {
      const a = b.equation.left.coeff;
      const c = b.equation.right.coeff;
      if (a.equals(c)) return false; // would not actually be one-solution
    }
    return true;
  }

  private toQuestion(ctx: GenerateContext, b: Built): Question {
    const topic = getTopic(TOPIC);
    const steps = this.assembleSteps(b);
    const hints = this.assembleHints(b);
    const id = `${TOPIC}-${ctx.seed}-${b.family}`;

    if (b.category === "one") {
      return {
        id,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family: b.family,
        difficulty: ctx.difficulty,
        prompt: { text: b.displayText, latex: b.displayLatex },
        answerType: "number",
        answer: { kind: "number", value: b.solution as Rational },
        steps,
        hints,
        subskills: topic.subskills,
        math: b.equation,
      };
    }
    // Special categories are answered as "special".
    return {
      id,
      generator: TOPIC,
      generatorVersion: VERSION,
      seed: ctx.seed,
      unit: topic.unit,
      topic: TOPIC,
      family: b.family,
      difficulty: ctx.difficulty,
      prompt: { text: b.displayText, latex: b.displayLatex },
      answerType: "special",
      answer: {
        kind: "special",
        value: b.category === "none" ? "no-solution" : "infinitely-many",
      },
      steps,
      hints,
      subskills: topic.subskills,
      math: b.equation,
    };
  }

  private assembleSteps(b: Built): SolutionStep[] {
    const steps: SolutionStep[] = [];
    if (b.distributionStep) steps.push(b.distributionStep);
    steps.push(...deriveSolutionSteps(b.equation, b.variable));
    return steps;
  }

  private assembleHints(b: Built): Hint[] {
    const hints: Hint[] = [];
    if (b.distributionStep) {
      hints.push({
        level: 1,
        text: "Distribute across the parentheses on both sides before moving any terms.",
      });
    }
    hints.push({
      level: hints.length + 1,
      text: "Collect the variable terms on one side and the constants on the other.",
    });
    hints.push({
      level: hints.length + 1,
      text: "Divide both sides by the coefficient of the variable to isolate it.",
    });
    return hints;
  }
}

// --- small display helpers (module-private) ------------------------------

/**
 * For a term `- cc(...)` in F6, fold the sign of cc into the operator so we
 * never render "- -5(...)". Returns e.g. "- 5", "+ 5", "- " (cc = 1), "+ ".
 */
function groupOp(cc: Rational): string {
  // The structural operation subtracts cc(...). Effective sign is -cc.
  const eff = cc.neg();
  const op = eff.isNegative() ? "-" : "+";
  const mag = eff.abs();
  if (mag.equals(Rational.ONE)) return `${op} `;
  return `${op} ${mag.toString()}`;
}

/** Factor in front of a parenthesized group, e.g. "3", "-", "" for 1. */
function factorLatex(c: Rational): string {
  if (c.equals(Rational.ONE)) return "";
  if (c.equals(Rational.of(-1n))) return "-";
  return c.toLatex();
}
function factorText(c: Rational): string {
  if (c.equals(Rational.ONE)) return "";
  if (c.equals(Rational.of(-1n))) return "-";
  return c.toString();
}

function coeff(c: Rational, v: string): string {
  if (c.equals(Rational.ONE)) return v;
  if (c.equals(Rational.of(-1n))) return `-${v}`;
  return `${c.toLatex()}${v}`;
}
function coeffText(c: Rational, v: string): string {
  if (c.equals(Rational.ONE)) return v;
  if (c.equals(Rational.of(-1n))) return `-${v}`;
  return `${c.toString()}${v}`;
}
function plusConst(r: Rational): string {
  return r.isNegative() ? `- ${r.neg().toLatex()}` : `+ ${r.toLatex()}`;
}
function plusConstText(r: Rational): string {
  return r.isNegative() ? `- ${r.neg().toString()}` : `+ ${r.toString()}`;
}
function exprRight(c: Rational, k: Rational, v: string): string {
  const parts = [coeff(c, v)];
  if (!k.isZero()) parts.push(plusConst(k));
  return parts.join(" ");
}
function exprRightText(c: Rational, k: Rational, v: string): string {
  const parts = [coeffText(c, v)];
  if (!k.isZero()) parts.push(plusConstText(k));
  return parts.join(" ");
}

/** The singleton generator with default classroom calibration. */
export const variablesBothSides = new VariablesBothSidesGenerator();
